/**
 * Colour palettes defined by an organization (brand colours). Members pick
 * from them in every colour field of the editor; tools can read them too.
 */
export const MAX_PALETTES = 50;
export const MAX_PALETTE_COLORS = 32;

/** The palette's colours as unique lowercase #rrggbb (#rgb is expanded); anything else is dropped. */
export function normalizePaletteColors(colors: readonly unknown[]): string[] {
  const out: string[] = [];
  for (const c of colors) {
    if (typeof c !== 'string') continue;
    let v = c.trim().toLowerCase();
    if (!v.startsWith('#')) v = `#${v}`;
    if (/^#[0-9a-f]{3}$/.test(v)) v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
    if (/^#[0-9a-f]{6}$/.test(v) && !out.includes(v)) out.push(v);
    if (out.length >= MAX_PALETTE_COLORS) break;
  }
  return out;
}

/** A palette name trimmed, or an error message. */
export function checkPaletteName(name: string): { name: string } | { error: string } {
  const n = name.trim();
  if (!n) return { error: 'Name the palette' };
  if (n.length > 60) return { error: 'Palette names are at most 60 characters' };
  return { name: n };
}

/** Colours a new palette starts with. */
export const STARTER_PALETTE = ['#111827', '#ffffff', '#2563eb', '#f59e0b'];

/**
 * Colours in pasted text: hex codes separated by spaces, commas, semicolons,
 * pipes or new lines, with or without "#" (or "0x"), in quotes or brackets
 * (e.g. a CSS or JSON list). `skipped` lists the pieces that are not colours.
 */
export function parseHexList(text: string): { colors: string[]; skipped: string[] } {
  const found: string[] = [];
  const skipped: string[] = [];
  for (const raw of text.split(/[\s,;|]+/)) {
    const token = raw.replace(/^[\s"'`([{]+|[\s"'`)\]}:.]+$/g, '');
    if (!token) continue;
    const hex = token.replace(/^(#|0x)/i, '');
    if (/^[0-9a-f]{3}$|^[0-9a-f]{6}$/i.test(hex)) found.push(`#${hex}`);
    else skipped.push(raw);
  }
  return { colors: normalizePaletteColors(found), skipped };
}
